<script setup lang="ts">
import { captureException } from '@sentry/vue';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import { ref } from 'vue';
import { useDebugUser } from '@/composables/useDebugUser';
import { db } from '../firebase';

definePage({
  name: 'support',
});
const faqs = ref([
  {
    question: 'How do I upload my simulator data?',
    answer:
      'Navigate to the Upload Data page, click the upload button, and select your CSV file from your simulator. We support major simulator formats including Trackman, GC Quad, and more.',
    isOpen: true,
  },
  {
    question: 'What file formats are supported?',
    answer:
      'We currently support CSV files from major golf simulators. Each file should contain shot data including ball speed, club head speed, launch angle, and other relevant metrics.',
    isOpen: true,
  },
  {
    question: 'How does the improvement cycle work?',
    answer:
      'We\'ve designed a simple way to help you improve your golf game: First, upload your swing data from a practice session. Our AI analyzes your performance and creates a detailed report. From there, we generate a personalized Practice Plan with specific drills tailored to your needs. Record one session per drill, upload your next session and see how you\'ve improved! This creates a continuous cycle of feedback and improvement for your golf game.',
    isOpen: true,
  },
  {
    question: 'How are shot ratings calculated?',
    answer:
      'Shot ratings are calculated using our proprietary AI algorithm that considers multiple factors including ball speed, launch conditions, and consistency across sessions.',
    isOpen: true,
  },
  {
    question: 'How often are recommendations updated?',
    answer:
      'Recommendations are updated after each new session upload and weekly to provide you with the most relevant practice suggestions based on your recent performance.',
    isOpen: true,
  },
]);

const supportForm = ref({
  subject: '',
  message: '',
  priority: 'low',
});

const toast = useToast();
const user = useDebugUser();

function toggleFaq(index: number) {
  faqs.value[index].isOpen = !faqs.value[index].isOpen;
}

async function submitSupportRequest() {
  try {
    const supportCollection = collection(db, 'support_tickets');
    await addDoc(supportCollection, {
      ...supportForm.value,
      userId: user.value?.uid,
      userEmail: user.value?.email,
      status: 'new',
      createdAt: serverTimestamp(),
    });

    // Reset form
    supportForm.value = {
      subject: '',
      message: '',
      priority: 'low',
    };

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Support request submitted successfully',
      life: 3000,
    });
  }
  catch (error) {
    console.error('Error submitting support request:', error);
    captureException(error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to submit support request. Please try again.',
      life: 3000,
    });
  }
}

const appVersion = import.meta.env.VITE_VERSION || 'unknown';
</script>

<template>
  <div class="support-container p-6">
    <h1 class="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
      Support Center
    </h1>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- FAQ Section -->
      <div class="lg:col-span-2">
        <div class="bg-white p-6 rounded-lg shadow">
          <h2 class="text-xl font-semibold mb-6">
            Frequently Asked Questions
          </h2>

          <div id="faq-list" class="space-y-4">
            <div v-for="(faq, index) in faqs" :key="index" class="faq-item">
              <Button
                @click="toggleFaq(index)"
              >
                <span class="font-medium text-left">{{ faq.question }}</span>
                <span class="transform transition-transform" :class="{ 'rotate-180': faq.isOpen }"> ▼ </span>
              </Button>
              <div v-show="faq.isOpen" class="p-4 text-gray-600">
                {{ faq.answer }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Contact Form -->
      <div class="lg:col-span-1">
        <div class="bg-white p-6 rounded-lg shadow">
          <h2 class="text-xl font-semibold mb-6">
            Contact Support
          </h2>
          <form class="space-y-4" @submit.prevent="submitSupportRequest">
            <div class="form-group">
              <label class="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <select v-model="supportForm.subject" class="w-full border rounded-lg p-2" required>
                <option value="">
                  Select a subject
                </option>
                <option value="technical">
                  Technical Issue
                </option>
                <option value="account">
                  Account Related
                </option>
                <option value="billing">
                  Billing Question
                </option>
                <option value="feature">
                  Feature Request
                </option>
                <option value="other">
                  Other
                </option>
              </select>
            </div>

            <div class="form-group">
              <label class="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                v-model="supportForm.message"
                rows="6"
                class="w-full border rounded-lg p-2"
                placeholder="Describe your issue or question..."
                required
              />
            </div>

            <div class="form-group">
              <label class="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select v-model="supportForm.priority" class="w-full border rounded-lg p-2" required>
                <option value="low">
                  Low
                </option>
                <option value="medium">
                  Medium
                </option>
                <option value="high">
                  High
                </option>
              </select>
            </div>

            <Button
              type="submit"
              class="w-full bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Submit Request
            </Button>
          </form>
        </div>
        <p class="mt-4 text-sm text-gray-500">
          App version: {{ appVersion }}
        </p>
      </div>
    </div>
  </div>
</template>

<style>
.support-container {
  max-width: 1200px;
  margin: 0 auto;
}

#faq-list {
  .p-button {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 1rem;
  }
}
</style>
